import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';

export interface ScientificMarkdownProps {
  content: string;
}

/**
 * Custom renderer only for `a` — every other markdown construct (headings,
 * paragraphs, lists, tables, blockquotes, bold/italic, inline code, hr)
 * uses react-markdown's own default output, which is already the correct
 * semantic HTML element (`<h2>`, `<p>`, `<ul>`, `<table>`, ...). Styling for
 * all of them lives in ScientificEntryDetailPage.css under
 * `.dma-scientific-detail__content`, as descendant selectors — this
 * component only decides *what element* each link becomes, not how
 * anything looks.
 *
 * A link becomes a React Router `<Link>` (client-side navigation, no full
 * page reload) when its href starts with '/' — i.e. it points somewhere
 * else on this site, like `/mineralogy/quartz`. Anything else (an external
 * URL) renders as a plain `<a target="_blank" rel="noopener noreferrer">`.
 */
const components: Components = {
  a({ href, children }) {
    if (href?.startsWith('/')) {
      return <Link to={href}>{children}</Link>;
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
};

/**
 * Renders a scientific-content `description` (markdown, stored as plain
 * text in Supabase) as real, semantic HTML — never
 * `dangerouslySetInnerHTML`. react-markdown parses the markdown into a
 * syntax tree and renders every node through actual React elements, so
 * there is no raw-HTML injection surface: arbitrary `<script>` or `<img
 * onerror=...>` typed into the admin's textarea is rendered as inert text,
 * not executed.
 *
 * `remark-gfm` adds the GitHub-Flavored-Markdown extensions the admin form
 * README documents as supported — tables, strikethrough, autolinks — which
 * base CommonMark (react-markdown's default) does not include on its own.
 */
export function ScientificMarkdown({ content }: ScientificMarkdownProps) {
  return (
    <div className="dma-scientific-detail__content">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
